import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Clock, ChevronRight, CheckCircle2, ShieldCheck, CreditCard, FileCheck } from "lucide-react";
import { notificationsApi } from "../../lib/api";

const ACTION_ROUTE = { kyc: "/app/kyc", permit: "/app/kyc", payout: "/app/payouts" };
const ACTION_ICON = { kyc: FileCheck, permit: ShieldCheck, payout: CreditCard };

const TONE = {
  high: { ring: "border-red-200", chip: "bg-red-50 text-red-700", Icon: AlertTriangle },
  medium: { ring: "border-amber-200", chip: "bg-amber-50 text-amber-700", Icon: AlertTriangle },
  info: { ring: "border-brand-200", chip: "bg-brand-50 text-brand", Icon: Clock },
};

// Action-items hub for drivers & restaurant owners (KYC, permit, payout, rejected docs).
export default function PendingTasks() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState(null);

  useEffect(() => {
    notificationsApi.pendingTasks().then(setTasks).catch(() => setTasks([]));
  }, []);

  if (tasks === null) return null;
  if (tasks.length === 0) {
    return (
      <div className="mb-6 flex items-center gap-3 rounded-2xl border border-graphite-200 bg-graphite-50 p-4" data-testid="pending-tasks-empty">
        <CheckCircle2 className="h-5 w-5 shrink-0 text-graphite" />
        <p className="text-sm font-medium text-ink">You're all set — no pending tasks.</p>
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-2xl border border-surface-300 bg-white p-5" data-testid="pending-tasks">
      <div className="mb-3 flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-600" />
        <h3 className="text-sm font-semibold text-ink">Action needed ({tasks.length})</h3>
      </div>
      <ul className="space-y-3">
        {tasks.map((t) => {
          const tone = TONE[t.severity] || TONE.info;
          const ActIcon = ACTION_ICON[t.action] || FileCheck;
          return (
            <li key={t.key}>
              <button
                type="button"
                onClick={() => navigate(ACTION_ROUTE[t.action] || "/app/profile")}
                className={`flex w-full items-center gap-3 rounded-xl border ${tone.ring} bg-surface-50 p-3 text-left transition-colors hover:bg-surface-100`}
                data-testid={`pending-task-${t.key}`}
              >
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone.chip}`}><ActIcon className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-ink">{t.title}</span>
                  <span className="block truncate text-xs text-ink-soft">{t.detail}</span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
